import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

import { IFetchUserDetailsParams, ITabIndex } from "@/models/chat";

import {
  ISearchChatRequest,
  ISearchChat,
} from "@/models/ApiRequestResponse/chat";
import { GBSCError } from "@/models/GBSCError";

import { fetchUserDetails, searchChatByName } from "@/utils/apis/chat";

import { RootState } from "@/store/store";

export const fetchUser = createAsyncThunk(
  "chat/fetch-user-details",
  async ({
    user_ids,
    doctor_ids,
    ip_dash_id,
    device_token_user_id,
    appointment_ids,
    member_ids,
  }: IFetchUserDetailsParams) => {
    const response = await fetchUserDetails({
      user_ids,
      doctor_ids,
      ip_dash_id,
      device_token_user_id,
      appointment_ids,
      member_ids,
    });
    return response;
  }
);

type ChatResultItem = any;

export const searchChat = createAsyncThunk<
  ChatResultItem[],
  ISearchChatRequest,
  { rejectValue: string }
>(
  "chat/search-chat",
  async (params: ISearchChatRequest, { rejectWithValue }) => {
    try {
      const response = await searchChatByName(params);

      if (response instanceof GBSCError) {
        // Use rejectWithValue to handle controlled errors
        return rejectWithValue(response.message);
      }

      // Map the results within the thunk
      const mappedResults = response.result.map((chat: ISearchChat) => ({
        uniqueId: chat.memberId,
        user_details: {
          name: chat.name,
        },
        ...chat,
      }));
      return mappedResults;
    } catch (error: any) {
      return rejectWithValue(error.message || "An unexpected error occurred");
    }
  }
);

const initialState: any = {
  selectedChat: null,
  messageNotification: null,
  userDetails: [],
  isInitiateFetchUserDetail: true,
  isUserDetailLoading: false,
  isChatSearchOn: false,
  chatList: [],
  isChatDialogOpen: false,
  quoteMessage: null,
  inPatientFilter: {
    selectedRoomId: "",
    selectedhospitalId: "",
  },
  searchChatList: [],
  isSearchLoading: false,
  activeChatTab: ITabIndex.APPOINTMENTS,
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    resetChatState: () => initialState,
    setSelectedChat: (
      state,
      action: PayloadAction<Record<string, unknown>>
    ) => {
      state.selectedChat = action.payload;
    },
    setInitiateFetchUserDetail: (state, action: PayloadAction<boolean>) => {
      state.isInitiateFetchUserDetail = action.payload;
    },
    setMessageNotification: (state, action) => {
      state.messageNotification = action.payload;
    },
    setChatSearchOn: (state, action: PayloadAction<boolean>) => {
      state.isChatSearchOn = action.payload;
    },
    setActiveChatTab: (state, action: PayloadAction<ITabIndex>) => {
      state.activeChatTab = action.payload;
    },
    setChatList: (state, action: PayloadAction<any[]>) => {
      state.chatList = action.payload;
    },
    setChatDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isChatDialogOpen = action.payload;
    },
    setQuoteMessage: (state, action: PayloadAction<any>) => {
      state.quoteMessage = action.payload;
    },
    setInPatientFilter: (state, action: PayloadAction<any>) => {
      state.inPatientFilter = action.payload;
    },
    setSearchList: (state, action: PayloadAction<any[]>) => {
      state.searchChatList = action.payload;
    },
    resetChat: () => initialState,
    clearSearchChatList: (state) => {
      state.searchChatList = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.isUserDetailLoading = true;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.isUserDetailLoading = false;
        const prev = state.userDetails || {};
        const incoming = action.payload.result || {};

        // Create a new merged object
        const merged: Record<string, any> = { ...prev };

        Object.keys(incoming).forEach((key) => {
          if (Array.isArray(incoming[key])) {
            // Merge arrays and remove duplicates based on `id` (or patientId)
            const prevArr = prev[key] || [];
            const newArr = incoming[key] || [];

            const mergedArr = [...prevArr, ...newArr].filter(
              (item, index, self) =>
                index ===
                self.findIndex((t) => {
                  // Change "id" to patientId if needed
                  return t.id
                    ? t.id === item.id
                    : t.patientId === item.patientId;
                })
            );

            merged[key] = mergedArr;
          } else {
            // For non-arrays, just replace
            merged[key] = incoming[key];
          }
        });

        state.userDetails = merged;
      })
      .addCase(fetchUser.rejected, (state) => {
        state.isUserDetailLoading = false;
      })
      .addCase(searchChat.pending, (state) => {
        state.isSearchLoading = true;
      })
      .addCase(searchChat.fulfilled, (state, action) => {
        state.isSearchLoading = false;
        const { offset } = action.meta.arg;

        if (offset === 0) {
          state.searchChatList = action.payload;
        } else {
          state.searchChatList.push(...action.payload);
        }
      })
      .addCase(searchChat.rejected, (state) => {
        state.isSearchLoading = false;
        state.searchChatList = [];
      });
  },
});

const selectChat = (state: RootState) => state.chat;

export const chat = createSelector([selectChat], (selectChat) => {
  return {
    selectedChat: selectChat.selectedChat,
    messageNotification: selectChat.messageNotification,
    userDetails: selectChat.userDetails,
    isInitiateFetchUserDetail: selectChat.isInitiateFetchUserDetail,
    isUserDetailLoading: selectChat.isUserDetailLoading,
    isChatSearchOn: selectChat.isChatSearchOn,
    chatList: selectChat.chatList,
    isChatDialogOpen: selectChat.isChatDialogOpen,
    quoteMessage: selectChat.quoteMessage,
    inPatientFilter: selectChat.inPatientFilter,
    activeChatTab: selectChat.activeChatTab,
    searchChatList: selectChat.searchChatList,
    isSearchLoading: selectChat.isSearchLoading,
  };
});

export const {
  setSelectedChat,
  resetChatState,
  setInitiateFetchUserDetail,
  setMessageNotification,
  setChatSearchOn,
  setChatList,
  setChatDialogOpen,
  setQuoteMessage,
  setInPatientFilter,
  setActiveChatTab,
  resetChat,
  clearSearchChatList,
  setSearchList,
} = chatSlice.actions;

export default chatSlice.reducer;
